import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject, Subscription } from 'rxjs';
import { AuthApi } from './auth.api';
import { AuthStore } from './auth.store';
import { decodeJwtPayload } from './jwt';

/**
 * Injectable service owning all token refresh state.
 *
 * Core layer — infrastructure concern.
 *
 * Responsibilities:
 *   - Handle 401 errors from the interceptor via handle401()
 *   - Schedule proactive refresh ~60s before JWT expiry via effect()
 *   - Queue concurrent 401s via ReplaySubject (one refresh at a time)
 *   - Clear session and redirect on refresh failure
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService implements OnDestroy {
  private readonly authApi = inject(AuthApi);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);

  /** Public signal the interceptor can read to check refresh state */
  readonly isRefreshing = signal(false);

  /** Replays last token to queued subscribers; resets on error */
  private refreshSubject = new ReplaySubject<string>(1);
  private refreshSubscription: Subscription | null = null;

  constructor() {
    this.scheduleProactiveRefresh();
  }

  /**
   * Called by interceptor on 401.
   * If no refresh is in progress, triggers one.
   * Returns ReplaySubject so interceptor can switchMap to get the new token.
   * Phase 53 — skip refresh when needsOnboarding true (D-06 defense-in-depth)
   */
  handle401(): ReplaySubject<string> {
    const needsFn = (this.authStore as unknown as { needsOnboarding?: unknown })['needsOnboarding'];
    if (typeof needsFn === 'function' && (needsFn as () => boolean)()) {
      const sub = new ReplaySubject<string>(1);
      sub.error(new Error('onboarding incomplete — skip refresh'));
      return sub;
    }
    if (!this.isRefreshing()) {
      this.startRefresh();
    }
    return this.refreshSubject;
  }

  /**
   * Public method to cancel in-flight refresh and reset state.
   * Called by LogoutService during logout sequence.
   * Does NOT clear auth store — that is LogoutService's responsibility.
   */
  stopRefresh(): void {
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = null;
    this.refreshSubject = new ReplaySubject<string>(1);
    this.isRefreshing.set(false);
  }

  /**
   * Proactive refresh: effect() reacts to accessToken() changes.
   * Schedules a setTimeout ~60s before JWT expiry.
   * onCleanup cancels the timer when the signal changes or component is destroyed.
   */
  private scheduleProactiveRefresh(): void {
    effect((onCleanup) => {
      const maybeFn = (this.authStore as unknown as { accessToken?: unknown })['accessToken'];
      const token = typeof maybeFn === 'function' ? (maybeFn as () => string | null)() : null;
      if (!token) return;
      // Phase 53 — no proactive refresh when onboarding incomplete (D-06)
      const needsFn = (this.authStore as unknown as { needsOnboarding?: unknown })['needsOnboarding'];
      if (typeof needsFn === 'function' && (needsFn as () => boolean)()) return;

      let timerId: ReturnType<typeof setTimeout>;

      try {
        const payload = decodeJwtPayload<{ exp: number }>(token);
        const expiresAtMs = payload.exp * 1000;
        const refreshInMs = Math.max(expiresAtMs - Date.now() - 60_000, 0);

        timerId = setTimeout(() => {
          if (!this.isRefreshing()) {
            this.startRefresh();
          }
        }, refreshInMs);
      } catch {
        // Invalid token — don't schedule refresh
      }

      onCleanup(() => {
        if (timerId!) clearTimeout(timerId);
      });
    });
  }

  private startRefresh(): void {
    // Phase 53 — defense-in-depth: skip if needsOnboarding true even if handle401 missed
    const needsFn = (this.authStore as unknown as { needsOnboarding?: unknown })['needsOnboarding'];
    if (typeof needsFn === 'function' && (needsFn as () => boolean)()) {
      this.isRefreshing.set(false);
      this.refreshSubject.error(new Error('skip — onboarding incomplete'));
      this.refreshSubject = new ReplaySubject<string>(1);
      return;
    }
    this.isRefreshing.set(true);
    this.refreshSubscription = this.authApi.refresh().subscribe({
      next: (res) => {
        this.authStore.updateToken(res.accessToken);
        this.refreshSubject.next(res.accessToken);
        // DON'T complete — subject stays open for next cycle
        this.isRefreshing.set(false);
      },
      error: (err) => {
        this.refreshSubject.error(err);
        // Reset subject for next attempt
        this.refreshSubject = new ReplaySubject<string>(1);
        this.isRefreshing.set(false);
        this.authStore.clearSession();
        this.router.navigate(['/login'], {
          queryParams: { session_expired: '1' },
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }
}
